import type {
  ForecastTraderContractSearchResponse,
  ForecastTraderDetailedMarket,
  ForecastTraderMassContractDetailsResponse,
} from '@/types/forecasttrader.types';
import type {
  PredictionClosingResponse,
  PredictionContractListResponse,
  PredictionProductsResponse,
} from '@/types/prediction.types';
import { useProductHub } from '@/composables/useProductHub';

// Single sanctioned exchange when the Product Hub is gated off. Any other
// exchange (KALSHI, FORECASTEX, etc.) gets stripped from API responses.
const FORECAST_X = 'FORECASTX';

/**
 * True when API responses should be restricted to FORECASTX-only entries
 * (i.e. the Product Hub is disabled for this user).
 *
 * Safe to call from TanStack Query `select` callbacks: `useProductHub`'s
 * `computed` has no reactive dependencies, so it just reads the AB flag /
 * hostname synchronously and returns.
 */
export function shouldRestrictToForecastX(): boolean {
  return !useProductHub().isProductHubEnabled.value;
}

const isOtherExchange = (exchange: string | undefined): boolean => exchange !== FORECAST_X;

export function filterSearchToForecastX(
  res: ForecastTraderContractSearchResponse,
): ForecastTraderContractSearchResponse {
  if (!shouldRestrictToForecastX()) return res;
  return {
    ...res,
    contracts: res.contracts.filter((c) => !isOtherExchange(c.exchange)),
  };
}

/**
 * Removes non-FORECASTX markets from any `{ markets: ForecastTraderDetailedMarket[] }`
 * response. Also strips non-FORECASTX entries from `markets[i].exchanges?` and
 * removes the corresponding entry from `markets[i].productConids?` (those
 * arrays are positionally aligned).
 */
export function filterMarketsToForecastX<T extends { markets: ForecastTraderDetailedMarket[] }>(res: T): T {
  if (!shouldRestrictToForecastX()) return res;
  const filtered = res.markets
    .filter((m) => !isOtherExchange(m.exchange))
    .map((m): ForecastTraderDetailedMarket => {
      if (!m.exchanges?.length) return m;
      const keepIndices: number[] = [];
      const exchanges: string[] = [];
      m.exchanges.forEach((ex, i) => {
        if (!isOtherExchange(ex)) {
          keepIndices.push(i);
          exchanges.push(ex);
        }
      });
      const productConids = m.productConids
        ? keepIndices.map((i) => m.productConids![i]).filter((v) => v !== undefined)
        : m.productConids;
      return { ...m, exchanges, productConids };
    });
  return { ...res, markets: filtered };
}

export function filterPredictionProductsToForecastX(res: PredictionProductsResponse): PredictionProductsResponse {
  if (!shouldRestrictToForecastX()) return res;
  const complex_products = res.complex_products
    .map((cp) => {
      const products = cp.products.filter((p) => !isOtherExchange(p.exchange));
      const complex_contracts = cp.complex_contracts
        .map((cc) => ({
          ...cc,
          contracts: cc.contracts.filter((c) => !isOtherExchange(c.exchange)),
        }))
        .filter((cc) => cc.contracts.length > 0);
      return { ...cp, products, complex_contracts };
    })
    .filter((cp) => cp.products.length > 0 && cp.complex_contracts.length > 0);
  return { ...res, complex_products };
}

export function filterContractListToForecastX(res: PredictionContractListResponse): PredictionContractListResponse {
  if (!shouldRestrictToForecastX()) return res;
  const complex_contracts = res.complex_contracts
    .map((cc) => ({
      ...cc,
      contracts: cc.contracts.filter((c) => !isOtherExchange(c.exchange)),
    }))
    .filter((cc) => cc.contracts.length > 0);
  return { ...res, complex_contracts };
}

export function filterClosingToForecastX(res: PredictionClosingResponse): PredictionClosingResponse {
  if (!shouldRestrictToForecastX()) return res;
  const contracts = res.contracts
    .map((c) => ({ ...c, exchanges: c.exchanges.filter((ex) => !isOtherExchange(ex)) }))
    .filter((c) => c.exchanges.length > 0);
  return { ...res, contracts };
}

export function filterMassDetailsToForecastX(
  res: ForecastTraderMassContractDetailsResponse,
): ForecastTraderMassContractDetailsResponse {
  if (!shouldRestrictToForecastX()) return res;
  return {
    ...res,
    contracts: res.contracts.filter((c) => !isOtherExchange(c.exchange)),
  };
}
